import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

const DISMISS_KEY = 'pwa-prompt-dismissed-at';
const DISMISS_TTL_MS = 1000 * 60 * 60 * 24 * 7; // 7 days — reprompt returning visitors

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  const stillDismissed = () => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (!raw) {
        // Migrate legacy forever-dismiss value → 7-day window from now
        if (localStorage.getItem('pwa-prompt-dismissed') === 'true') {
          localStorage.setItem(DISMISS_KEY, String(Date.now()));
          localStorage.removeItem('pwa-prompt-dismissed');
          return true;
        }
        return false;
      }
      const ts = Number(raw) || 0;
      return ts && Date.now() - ts < DISMISS_TTL_MS;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);

      if (!stillDismissed()) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show iOS instructions if applicable
    if (ios && !standalone && !stillDismissed()) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    // Global manual trigger — any button on the site can call
    // window.__rvOpenInstallPrompt() to force the install UI open.
    // Bypasses Chrome's engagement heuristics + our 7-day dismiss window.
    window.__rvOpenInstallPrompt = () => {
      // Clear dismissal so the banner shows even if they dismissed earlier
      try { localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
      try { localStorage.removeItem('pwa-prompt-dismissed'); } catch { /* ignore */ }
      if (standalone) {
        alert('The app is already installed on this device.');
        return;
      }
      setShowPrompt(true);
    };

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      try { delete window.__rvOpenInstallPrompt; } catch { /* ignore */ }
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('PWA install outcome:', outcome);
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // 7-day dismissal window — after that we re-prompt returning visitors
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  // Don't show if already installed or no prompt available
  if (isStandalone || !showPrompt) return null;

  return (
    <div
      className="fixed left-3 right-[80px] md:bottom-4 md:right-4 md:left-auto md:w-80 z-[2147483001] animate-in slide-in-from-bottom-5 duration-300"
      style={{ bottom: '150px' }}
      data-testid="pwa-install-prompt"
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600"
          data-testid="pwa-dismiss"
        >
          <X size={18} />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shrink-0">
            <Smartphone className="text-white" size={24} />
          </div>
          <div className="flex-1 pr-4">
            <h3 className="font-bold text-gray-900 dark:text-white text-sm">
              Install RV Match AI
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {isIOS 
                ? 'Tap the share button, then "Add to Home Screen"'
                : 'Get quick access from your home screen'
              }
            </p>
          </div>
        </div>

        {!isIOS && deferredPrompt && (
          <Button 
            onClick={handleInstall}
            className="w-full mt-3 bg-orange-500 hover:bg-orange-600 text-white h-9 text-sm"
            data-testid="pwa-install-btn"
          >
            <Download size={16} className="mr-2" />
            Install App
          </Button>
        )}

        {isIOS && (
          <div className="mt-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
              <span>1. Tap</span>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"/>
              </svg>
              <span>Share</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 mt-1">
              <span>2. Scroll and tap "Add to Home Screen"</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
